import React, { useCallback, useMemo } from 'react'
import { Autocomplete, TextField, IconButton, Tooltip, Box, SxProps, Theme } from '@mui/material'
import { useField } from 'formik'
import { getReadableIAB, useIABSelectOptions } from '../../utils/hooks/useIABSelectOptions'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { isNil } from 'lodash-es'
import { AutoCompleteFieldProps } from './AutoCompleteField'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import { compactFormFieldSx } from './DisplayedItem'

const toolbarSelectLikeAutocompleteSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
    padding: '0 !important',
    paddingRight: '32px !important',
  },
  '& .MuiOutlinedInput-input, & .MuiAutocomplete-input': {
    padding: '8.5px 14px !important',
  },
  '& .MuiAutocomplete-endAdornment': {
    right: 9,
  },
}

export type IABAutoCompleteFieldProps = Pick<AutoCompleteFieldProps, 'name' | 'label'> & {
  withAllOption?: boolean
  required?: boolean
  onMouseLeave?: () => void
  variant?: 'filled' | 'outlined' | 'standard'
  sx?: SxProps<Theme>
  onFocus?: () => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  compact?: boolean
  inToolbar?: boolean
}

const IabAutoCompleteField: React.FC<IABAutoCompleteFieldProps> = ({
  label,
  name,
  withAllOption,
  required = false,
  onMouseLeave,
  variant = 'outlined',
  sx,
  onFocus,
  onBlur,
  compact = false,
  inToolbar = false,
}) => {
  const t = useAppTranslations()
  const [{ value }, meta, { setValue }] = useField(name)
  const hasError = Boolean(meta.touched && meta.error)
  const helperText = (meta.error && hasError && t(meta.error)) || (inToolbar ? '' : ' ')
  const isCompactLayout = compact || inToolbar
  const options = useIABSelectOptions()
  const [expandedOptions, setExpandedOptions] = React.useState<Set<string>>(new Set())

  const optionsMemo = useMemo(() => {
    if (withAllOption && options?.length) return [{ value: 'all', label: t('search.allIAB') }, ...options]
    else return options
  }, [options, withAllOption])

  const handleExpand = (event: React.MouseEvent, optionValue: string): void => {
    event.stopPropagation()
    setExpandedOptions((prevExpanded) => {
      const updatedSet = new Set(prevExpanded)
      if (updatedSet.has(optionValue)) updatedSet.delete(optionValue)
      else updatedSet.add(optionValue)

      return updatedSet
    })
  }

  const onChange = useCallback(
    (event, nextSelectedOption) => {
      setValue(nextSelectedOption?.value)
    },
    [setValue]
  )

  const getOptionLabel = useCallback(
    (option) => option?.label || optionsMemo.find((o) => option === o.value)?.label || '',
    [optionsMemo]
  )

  const resetExpandedOptions = useCallback(() => {
    if (value.includes('_')) setExpandedOptions(new Set([value.split('_')[0]]))
    else setExpandedOptions(new Set())
  }, [value])

  return (
    <Box onMouseLeave={onMouseLeave}>
      <Autocomplete
        value={value}
        onChange={onChange}
        options={optionsMemo}
        disableClearable
        onClose={resetExpandedOptions}
        onOpen={resetExpandedOptions}
        getOptionLabel={getOptionLabel}
        sx={inToolbar ? { ...toolbarSelectLikeAutocompleteSx, ...sx } : undefined}
        filterOptions={(options, { inputValue }) => {
          if (!inputValue) return options
          const matchedOptions = options.filter((option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase())
          )

          matchedOptions.forEach((option) => {
            const parentValue = option.value.split('_')[0]
            if (option.value.includes('_') && !expandedOptions.has(parentValue))
              setExpandedOptions((prev) => new Set(prev).add(parentValue))
          })

          return matchedOptions
        }}
        isOptionEqualToValue={(option, value) => !isNil(option) && option.value === (value as any)}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props
          if (option.value === 'all')
            return (
              <Box component="li" key={key} {...optionProps}>
                {option.label}
              </Box>
            )
          const isParent = !option.value.includes('_')
          const isExpanded = expandedOptions.has(option.value.split('_')[0])
          if (!isExpanded && !isParent) return null

          return (
            <Tooltip key={key} title={getReadableIAB(option.value)}>
              <Box
                component="li"
                {...optionProps}
                sx={{
                  ...(option.value.includes('_') ? { marginLeft: 4 } : {}),
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {isParent && (
                  <IconButton onClick={(event) => handleExpand(event, option.value)} size="small" sx={{ my: 0, py: 0 }}>
                    {isExpanded ? <ExpandLess sx={{ fontSize: '20px' }} /> : <ExpandMore sx={{ fontSize: '20px' }} />}
                  </IconButton>
                )}
                {option.label}
              </Box>
            </Tooltip>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            onFocus={onFocus}
            onBlur={onBlur}
            label={t(label)}
            variant={variant}
            size="small"
            margin={isCompactLayout ? 'none' : 'normal'}
            fullWidth
            required={required}
            sx={compact ? { ...compactFormFieldSx, ...(!inToolbar ? sx : undefined) } : !inToolbar ? sx : undefined}
            error={hasError}
            helperText={helperText}
            hiddenLabel={compact}
            slotProps={{
              ...params.slotProps,
              inputLabel: compact ? { sx: { display: 'none' } } : { shrink: true },
            }}
          />
        )}
        slotProps={{
          popper: {
            style: { minWidth: 300 },
          },
          ...(inToolbar
            ? {
                popupIndicator: {
                  sx: {
                    marginRight: 0,
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  },
                  style: {
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: 0,
                    height: 'auto',
                    minWidth: 0,
                    width: 'auto',
                  },
                },
              }
            : {}),
        }}
      />
    </Box>
  )
}

export default IabAutoCompleteField
